// รายงานเช็คชื่อหอก่อนนอน — อ่านอย่างเดียว
// หัวหน้าฝ่ายปกครอง (27) และผู้บริหาร (90) ดูได้ การมอบหมายครูหอพักอยู่ที่
// discipline/DisciplineDormTeachers.js ของหัวหน้าฝ่ายปกครอง
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput';
import { toThaiDate, toISODate } from '../utils/thaiDate';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';

const apiError = (error) => error?.response?.data?.message || 'ลองใหม่อีกครั้ง';

const timeOf = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
};

// นาทีระหว่างตอนที่ครูลงว่าไม่อยู่กับตอนที่พบตัว — ตัวเลขที่ต้องตอบเมื่อมีเรื่อง
const minutesBetween = (a, b) => {
  const ms = new Date(b) - new Date(a);
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 60000) : null;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

/* ── รายงาน ───────────────────────────────────────────────────────────────── */

const ReportTab = () => {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(toISODate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const { data: res } = await axiosInstance.get(`${API}/dormitory/night-check/report`, { params: { from, to } });
      setData(res);
    } catch (error) {
      Swal.fire('อ่านรายงานไม่สำเร็จ', apiError(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Row className="g-2 align-items-end mb-3">
        <Col xs={6} md={3}>
          <Form.Label className="fs-xs text-secondary mb-1">ตั้งแต่คืน</Form.Label>
          <ThaiDateInput value={from} max={to} onChange={setFrom} />
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="fs-xs text-secondary mb-1">ถึงคืน</Form.Label>
          <ThaiDateInput value={to} min={from} onChange={setTo} />
        </Col>
        <Col xs="auto">
          {loading && <Spinner size="sm" animation="border" />}
        </Col>
      </Row>

      {data && (
        <>
          <Card className="card-one mb-3">
            <Card.Header><Card.Title as="h6" className="mb-0">สรุปรายคืน</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 fs-sm">
                <thead>
                  <tr>
                    <th>คืน</th>
                    <th className="text-end">ยืนยันแล้ว (หลัง)</th>
                    <th className="text-end">นักเรียน</th>
                    <th className="text-end">อยู่</th>
                    <th className="text-end">ลา/ห้องพยาบาล</th>
                    <th className="text-end">ไม่อยู่</th>
                    <th className="text-end">ยังไม่พบตัว</th>
                    <th>ยืนยันล่าสุด</th>
                  </tr>
                </thead>
                <tbody>
                  {data.nights.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-secondary py-3">ไม่มีการเช็คชื่อในช่วงนี้</td></tr>
                  ) : data.nights.map((n) => (
                    <tr key={n.date}>
                      <td>{toThaiDate(n.date)}</td>
                      <td className="text-end">{n.houses_confirmed}</td>
                      <td className="text-end">{n.students}</td>
                      <td className="text-end">{n.present}</td>
                      <td className="text-end">{n.excused}</td>
                      <td className="text-end">{n.absent > 0 ? <span className="text-danger fw-semibold">{n.absent}</span> : 0}</td>
                      <td className="text-end">{n.missing > 0 ? <Badge bg="danger">{n.missing}</Badge> : '-'}</td>
                      <td>{timeOf(n.last_confirmed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="fs-xs text-secondary">
              หลังที่ไม่มีใครยืนยันจะไม่อยู่ในยอดนี้ คืนที่ไม่มีแถวเลยคือคืนที่ไม่มีการเช็คชื่อในระบบ
            </Card.Footer>
          </Card>

          <Card className="card-one mb-3">
            <Card.Header><Card.Title as="h6" className="mb-0">รายชื่อที่ไม่อยู่ในหอ ({data.absentees.length})</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 fs-sm">
                <thead>
                  <tr>
                    <th>คืน</th>
                    <th>นักเรียน</th>
                    <th>เขต / หลัง</th>
                    <th>ลงโดย</th>
                    <th>พบตัว</th>
                  </tr>
                </thead>
                <tbody>
                  {data.absentees.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-secondary py-3">ไม่มี</td></tr>
                  ) : data.absentees.map((a) => {
                    const mins = a.nc_found_at ? minutesBetween(a.nc_at, a.nc_found_at) : null;
                    return (
                      <tr key={`${a.date}-${a.st_id}`}>
                        <td>{toThaiDate(a.date)}</td>
                        <td>{a.st_name}<div className="fs-xs text-secondary">{a.st_idnumber}</div></td>
                        <td>{a.village_name} · {a.dorm_name}</td>
                        <td>{a.by_name}<div className="fs-xs text-secondary">{timeOf(a.nc_at)} น.{a.nc_reason ? ` · ${a.nc_reason}` : ''}</div></td>
                        <td>
                          {a.nc_found_at ? (
                            <>
                              <span className="text-success">{timeOf(a.nc_found_at)} น.</span>
                              {mins !== null && <span className="fs-xs text-secondary"> (หลัง {mins} นาที)</span>}
                              <div className="fs-xs text-secondary">{a.found_by_name}{a.nc_found_note ? ` · ${a.nc_found_note}` : ''}</div>
                            </>
                          ) : <Badge bg="danger">ยังไม่พบตัว</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="card-one">
            <Card.Header><Card.Title as="h6" className="mb-0">ไม่อยู่ในหอตั้งแต่ 2 คืนขึ้นไป</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 fs-sm">
                <thead>
                  <tr><th>นักเรียน</th><th className="text-end">จำนวนคืน</th><th>ล่าสุด</th></tr>
                </thead>
                <tbody>
                  {data.frequent.length === 0 ? (
                    <tr><td colSpan={3} className="text-center text-secondary py-3">ไม่มี</td></tr>
                  ) : data.frequent.map((f) => (
                    <tr key={f.st_id}>
                      <td>{f.st_name}<div className="fs-xs text-secondary">{f.st_idnumber}</div></td>
                      <td className="text-end fw-semibold">{f.nights}</td>
                      <td>{toThaiDate(f.last_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default function NightCheckReport() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">รายงานเช็คชื่อหอก่อนนอน</h4>
          <div className="fs-xs text-secondary">ใครไม่อยู่ในหอคืนไหน พบตัวเมื่อไร และใครไม่อยู่ซ้ำ</div>
        </div>
        <ReportTab />
        <Footer />
      </div>
    </>
  );
}
